import {
  TextNode,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  $applyNodeReplacement,
} from 'lexical'
import { type Color, zColor } from '@backend/lib/db/typeRoots/color'
import { type EffectiveTextStyle } from '@backend/lib/db/typeRoots/layoutNode'
import { colorToString } from '../color'

export type SerializedRichTextNode = Spread<
  {
    type: 'rich-text-run'
    version: 1
    fontFamily: string
    fontSize: number
    fontWeight: number
    fontStyle: 'normal' | 'italic'
    fontColor: Color
  },
  SerializedTextNode
>

/** A Lexical TextNode that carries the same five glyph-styling fields a
 *  RichTextRun does. Each instance is one styled segment of text in the
 *  canvas editor; switching styles mid-line means appending another
 *  RichTextNode with different fields. Avoids Lexical's bitmask formats and
 *  inline `style` string so our absolute-styling model stays canonical. */
export class RichTextNode extends TextNode {
  __fontFamily: string
  __fontSize: number
  __fontWeight: number
  __fontStyle: 'normal' | 'italic'
  __fontColor: Color

  static override getType(): string {
    return 'rich-text-run'
  }

  static override clone(node: RichTextNode): RichTextNode {
    return new RichTextNode(
      node.__text,
      {
        fontFamily: node.__fontFamily,
        fontSize: node.__fontSize,
        fontWeight: node.__fontWeight,
        fontStyle: node.__fontStyle,
        fontColor: node.__fontColor,
      },
      node.__key,
    )
  }

  constructor(text: string, style: EffectiveTextStyle, key?: NodeKey) {
    super(text, key)
    this.__fontFamily = style.fontFamily
    this.__fontSize = style.fontSize
    this.__fontWeight = style.fontWeight
    this.__fontStyle = style.fontStyle
    this.__fontColor = style.fontColor
  }

  override createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    applyStyleToDom(dom, this)
    return dom
  }

  override updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const updated = super.updateDOM(prevNode, dom, config)
    applyStyleToDom(dom, this)
    return updated
  }

  getRunStyle(): EffectiveTextStyle {
    const self = this.getLatest()
    return {
      fontFamily: self.__fontFamily,
      fontSize: self.__fontSize,
      fontWeight: self.__fontWeight,
      fontStyle: self.__fontStyle,
      fontColor: self.__fontColor,
    }
  }

  setRunStyle(style: Partial<EffectiveTextStyle>): this {
    const writable = this.getWritable()
    if (style.fontFamily !== undefined) writable.__fontFamily = style.fontFamily
    if (style.fontSize !== undefined) writable.__fontSize = style.fontSize
    if (style.fontWeight !== undefined) writable.__fontWeight = style.fontWeight
    if (style.fontStyle !== undefined) writable.__fontStyle = style.fontStyle
    if (style.fontColor !== undefined) writable.__fontColor = style.fontColor
    return writable
  }

  /** Lexical's base TextNode.splitText creates plain TextNodes for the
   *  post-split parts — the original style fields are dropped. Override so
   *  every split part inherits our run-style fields, mirroring what Figma
   *  does on selection-restyle. */
  override splitText(...splitOffsets: number[]): RichTextNode[] {
    const parts = super.splitText(...splitOffsets)
    const myStyle = this.getLatest().getRunStyle()
    const result: RichTextNode[] = []
    for (const part of parts) {
      if (part instanceof RichTextNode) {
        result.push(part)
      } else {
        const replacement = $createRichTextNode(part.getTextContent(), myStyle)
        part.replace(replacement)
        result.push(replacement)
      }
    }
    return result
  }

  override exportJSON(): SerializedRichTextNode {
    return {
      ...super.exportJSON(),
      type: 'rich-text-run',
      version: 1,
      fontFamily: this.__fontFamily,
      fontSize: this.__fontSize,
      fontWeight: this.__fontWeight,
      fontStyle: this.__fontStyle,
      fontColor: this.__fontColor,
    }
  }

  static override importJSON(serialized: SerializedRichTextNode): RichTextNode {
    const node = $createRichTextNode(serialized.text, {
      fontFamily: serialized.fontFamily,
      fontSize: serialized.fontSize,
      fontWeight: serialized.fontWeight,
      fontStyle: serialized.fontStyle,
      fontColor: zColor.parse(serialized.fontColor),
    })
    node.setFormat(serialized.format)
    node.setDetail(serialized.detail)
    node.setMode(serialized.mode)
    return node
  }
}

const applyStyleToDom = (dom: HTMLElement, node: RichTextNode) => {
  dom.style.fontFamily = node.__fontFamily
  dom.style.fontSize = `${node.__fontSize}px`
  dom.style.fontWeight = String(node.__fontWeight)
  dom.style.fontStyle = node.__fontStyle
  dom.style.color = colorToString(node.__fontColor)
}

export const $createRichTextNode = (text: string, style: EffectiveTextStyle): RichTextNode =>
  $applyNodeReplacement(new RichTextNode(text, style))

export const $isRichTextNode = (node: LexicalNode | null | undefined): node is RichTextNode =>
  node instanceof RichTextNode
